import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format, isPast, isToday } from "date-fns";
import { Plus, Trash2 } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import {
  useClientReminders,
  useCreateReminder,
  useDeleteReminder,
  useToggleReminder,
} from "@/features/clients/api";
import { useClientId } from "@/features/clients/detail/use-client-id";
import { HubToolbar } from "@/features/clients/detail/hub-toolbar";
import { reminderFormSchema, type ReminderFormValues } from "@/features/clients/schema";
import { cn } from "@/lib/utils";

export function ClientRemindersPage() {
  const clientId = useClientId();
  const { data: reminders = [], isLoading } = useClientReminders(clientId);
  const createReminder = useCreateReminder(clientId);
  const toggleReminder = useToggleReminder(clientId);
  const deleteReminder = useDeleteReminder(clientId);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const form = useForm<ReminderFormValues>({
    resolver: zodResolver(reminderFormSchema),
    defaultValues: { title: "", due_at: "", is_done: false },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    await createReminder.mutateAsync(values);
    form.reset({ title: "", due_at: "", is_done: false });
  });

  const open = reminders.filter((r) => !r.is_done);
  const done = reminders.filter((r) => r.is_done);

  return (
    <div>
      <HubToolbar title="Reminders" description="Follow-ups tied to this account." />

      <div className="mx-auto max-w-3xl space-y-3 p-4">
      <form
        onSubmit={onSubmit}
        className="flex flex-wrap items-center gap-2 rounded-lg border border-border bg-background p-2"
      >
        <Input
          {...form.register("title")}
          placeholder="Follow up on pending PO…"
          className="min-w-48 flex-1 border-0 bg-transparent focus-visible:ring-0"
        />
        <Input type="datetime-local" {...form.register("due_at")} className="w-52" />
        <Button type="submit" size="sm" disabled={createReminder.isPending}>
          <Plus />
          Add
        </Button>
      </form>
      {form.formState.errors.title ? (
        <p className="text-2xs text-destructive">{form.formState.errors.title.message}</p>
      ) : null}

      {isLoading ? (
        <p className="py-8 text-center text-xs text-muted-foreground">Loading…</p>
      ) : reminders.length === 0 ? (
        <EmptyState title="No reminders" description="Schedule the next follow-up." />
      ) : (
        <div className="space-y-4">
          <ReminderList
            items={open}
            onToggle={(id, is_done) => void toggleReminder.mutateAsync({ id, is_done })}
            onDelete={setDeletingId}
          />
          {done.length ? (
            <div className="space-y-1.5">
              <p className="section-title">Completed ({done.length})</p>
              <ReminderList
                items={done}
                onToggle={(id, is_done) => void toggleReminder.mutateAsync({ id, is_done })}
                onDelete={setDeletingId}
              />
            </div>
          ) : null}
        </div>
      )}

      <ConfirmDialog
        open={Boolean(deletingId)}
        onOpenChange={(v) => !v && setDeletingId(null)}
        title="Delete reminder?"
        confirmLabel="Delete"
        destructive
        loading={deleteReminder.isPending}
        onConfirm={async () => {
          if (!deletingId) return;
          await deleteReminder.mutateAsync(deletingId);
          setDeletingId(null);
        }}
      />
      </div>
    </div>
  );
}

function ReminderList({
  items,
  onToggle,
  onDelete,
}: {
  items: { id: string; title: string; due_at: string | null; is_done: boolean }[];
  onToggle: (id: string, isDone: boolean) => void;
  onDelete: (id: string) => void;
}) {
  if (!items.length) return null;

  return (
    <ul className="divide-y divide-border overflow-hidden rounded-lg border border-border bg-card">
      {items.map((reminder) => {
        const due = reminder.due_at ? new Date(reminder.due_at) : null;
        const overdue = due && !reminder.is_done && isPast(due) && !isToday(due);

        return (
          <li key={reminder.id} className="group flex items-center gap-2.5 px-3 py-2">
            <Checkbox
              checked={reminder.is_done}
              onCheckedChange={(v) => onToggle(reminder.id, Boolean(v))}
              aria-label={`Mark ${reminder.title} done`}
            />
            <span
              className={cn(
                "min-w-0 flex-1 truncate text-sm",
                reminder.is_done && "text-muted-foreground line-through",
              )}
            >
              {reminder.title}
            </span>
            <span
              className={cn(
                "shrink-0 text-2xs tabular",
                overdue ? "font-medium text-destructive" : "text-muted-foreground",
              )}
            >
              {due ? format(due, "dd MMM, HH:mm") : "No due date"}
            </span>
            <Button
              type="button"
              variant="ghost"
              size="icon-sm"
              aria-label="Delete reminder"
              className="opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 hover:text-destructive"
              onClick={() => onDelete(reminder.id)}
            >
              <Trash2 />
            </Button>
          </li>
        );
      })}
    </ul>
  );
}
